import { store } from "store";
import { setUserData, setLogoutState } from "store/userDataSlice";
import { msalInstance } from "msalInstance";
import { loginRequest } from "authConfig";

// Prevents multiple concurrent 403s from firing several interactive redirects.
let redirectInFlight = false;
// Circuit-breaker state: avoids an infinite refresh/re-fetch loop when the
// backend keeps returning 403 even after a fresh token.
let lastRefreshAt = 0;
let refreshInFlight: Promise<string | null> | null = null;
const REFRESH_COOLDOWN_MS = 30_000;

function forceReauth(): void {
  if (redirectInFlight) {
    return;
  }
  redirectInFlight = true;
  store.dispatch(setLogoutState());
  msalInstance.acquireTokenRedirect(loginRequest).catch(() => {
    redirectInFlight = false;
  });
}

async function refreshToken(): Promise<string | null> {
  const account =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

  if (!account) {
    forceReauth();
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
      forceRefresh: true,
    });

    if (result?.accessToken) {
      lastRefreshAt = Date.now();
      store.dispatch(
        setUserData({
          user_name: result.account?.username ?? "",
          name: result.account?.name ?? "",
          access_token: result.accessToken,
        })
      );
      return result.accessToken;
    }

    forceReauth();
    return null;
  } catch {
    forceReauth();
    return null;
  }
}

/**
 * Called when an API Gateway request returns 403. Attempts a silent forced token
 * refresh (at most once per cooldown) and, on success, returns the fresh access
 * token so the caller can retry once. A repeated 403 shortly after a refresh
 * means the token isn't the problem, so it returns null instead of looping.
 */
export async function reauthenticateOnForbidden(): Promise<string | null> {
  // Reuse an in-flight refresh so concurrent 403s trigger only one token call.
  if (refreshInFlight !== null) {
    return refreshInFlight;
  }

  // A 403 right after a successful refresh means re-minting won't help — stop.
  if (Date.now() - lastRefreshAt < REFRESH_COOLDOWN_MS) {
    return null;
  }

  refreshInFlight = refreshToken();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}
